import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Typography, getTypographyDefaultColor, Col, Row } from '@ag1/ui';
import CustomPortableText from 'shared/studio/PortableText/PortableText';
import { HeroQuotes as HeroQuotesProps } from 'types/studio/studio';
import { StudioBaseImageV2 } from 'shared/studio/StudioBaseImage/StudioBaseImage';
import clsx from 'clsx';
import { getThemeCSSClass } from 'shared/utils/getThemeCSSClass';
import { useHorizontalScrollToElement } from 'shared/hooks/useHorizontalScrollToElement';
import useEmblaCarousel from 'embla-carousel-react';
import styles from './HeroQuotesContent.module.scss';

type Props = Pick<HeroQuotesProps, 'quotesList' | 'theme' | 'bgColor'> & {
  activeTab: string | null;
  onTabClick: (id: string, title: string) => void;
  visibleOnPageLoad: boolean;
};

const HeroQuotesContent = ({
  quotesList = [],
  theme,
  activeTab,
  bgColor,
  onTabClick,
  visibleOnPageLoad
}: Props) => {
  const activeItemRef = useRef<HTMLButtonElement | null>(null);
  const innerNavRef = useRef<HTMLDivElement>(null);
  const [prevIndex, setPrevIndex] = useState<number | null>(0);
  const currentIndex = quotesList.findIndex((tab) => tab._key === activeTab);

  useEffect(() => {
    if (currentIndex !== -1) {
      setPrevIndex(currentIndex);
    }
  }, [currentIndex]);

  useHorizontalScrollToElement({
    containerRef: innerNavRef,
    itemRef: activeItemRef,
    dependency: activeTab
  });

  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: false,
    align: 'start',
    watchDrag: false
  });

  useEffect(() => {
    if (emblaApi) {
      const index = quotesList.findIndex((tab) => tab._key === activeTab);
      if (index !== -1) {
        emblaApi.scrollTo(index);
      }
    }
  }, [activeTab, emblaApi, quotesList]);

  const [carouselHeight, setCarouselHeight] = useState(0);
  const slideRefs = useRef(quotesList.map(() => React.createRef<HTMLDivElement>()));

  const updateCarouselHeight = useCallback(() => {
    if (emblaApi) {
      const index = emblaApi.selectedScrollSnap();
      const activeSlideRef = slideRefs.current[index];

      if (activeSlideRef && activeSlideRef.current) {
        setCarouselHeight(activeSlideRef.current.offsetHeight);
      }
    }
  }, [emblaApi, slideRefs]);

  useEffect(() => {
    if (emblaApi) {
      emblaApi.on('select', updateCarouselHeight);
      updateCarouselHeight();

      return () => emblaApi.off('select', updateCarouselHeight);
    }

    return () => {};
  }, [emblaApi, updateCarouselHeight]);

  return (
    <>
      <nav
        className={styles.nav}
        aria-label="hero-quotes-tab"
        role="navigation"
        data-variant="links"
        ref={innerNavRef}>
        <ul className={styles.list}>
          {quotesList?.map((item, index) => (
            <li key={item._key} className={clsx(styles.listItem, getThemeCSSClass(theme))}>
              <button
                type="button"
                onClick={() => {
                  if (item._key !== activeTab) {
                    onTabClick(item._key, item.navigationTitle);
                  }
                }}
                ref={item._key === activeTab ? activeItemRef : undefined}
                className={clsx(
                  styles.link,
                  item._key === activeTab && styles.active,
                  prevIndex !== null && index < prevIndex && styles.linkBefore,
                  bgColor === 'stone' && styles.stone,
                  getThemeCSSClass(theme)
                )}>
                <Typography variant="body" size="2" noPadding className={styles.navigationTab}>
                  {item.navigationTitle}
                </Typography>
              </button>
            </li>
          ))}
        </ul>
      </nav>
      {quotesList && (
        <div ref={emblaRef} className={styles.embla} style={{ height: `${carouselHeight}px` }}>
          <div className={styles.emblaContainer}>
            {quotesList.map((item, index) => (
              <div ref={slideRefs.current[index]} className={styles.emblaSlide} key={item._key}>
                <Row className={clsx(styles.quoteItem)}>
                  <Col span={{ xxs: 12, m: 6, l: 5 }} className={styles.colText}>
                    <Typography
                      variant="subtitle"
                      size="2"
                      noPadding
                      weight="400"
                      className={styles.quote}
                      color={getTypographyDefaultColor(theme)}>
                      <CustomPortableText value={item.quote} tag="blockquote" />
                    </Typography>
                    <Typography
                      variant="caption"
                      size="1"
                      component="div"
                      noPadding
                      className={styles.caption}
                      color={getTypographyDefaultColor(theme)}>
                      {item.caption && <CustomPortableText value={item.caption} />}
                    </Typography>
                  </Col>
                  <Col span={{ xxs: 12, m: 6 }} offset={{ l: 1 }}>
                    <StudioBaseImageV2
                      image={item.quoteImage.image}
                      mobileImage={item.quoteImage.mobileImage}
                      tabletImage={item.quoteImage.tabletImage}
                      withMobileImage={item.quoteImage.withMobileImage}
                      sizes={{ xxs: 12, m: 6, xl: '700px' }}
                      eagerLoading={visibleOnPageLoad}
                      className={styles.image}
                    />
                  </Col>
                </Row>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
};

export default HeroQuotesContent;
